import { logEvent } from "@src/appV2/lib/analytics";

import { CHAT_ANALYTICS_EVENTS } from "../constants";

/**
 * Where the chat was opened from.
 * This helps understand user behavior and navigation patterns.
 */
export type ChatOpenLocation =
  | "shiftDetails"
  | "deepLink"
  | "browseProfessionals"
  | "browseWorkerDetails"
  | "myProfessionals"
  | "availableProfessionals";

interface LogChatOpenParams {
  /**
   * ID of the worker being chatted with
   */
  workerId: string;
  /**
   * ID of the workplace (admin user)
   */
  workplaceId: string;
  /**
   * Where the chat was opened from
   */
  location: ChatOpenLocation;
}

/**
 * Logs when a chat is opened.
 * Used for analytics to track chat usage patterns.
 */
export function logChatOpen(params: LogChatOpenParams) {
  const { workerId, workplaceId, location } = params;

  logEvent(CHAT_ANALYTICS_EVENTS.CHAT_OPENED, {
    workerId,
    workplaceId,
    workplaceUserId: workplaceId, // Keep compatibility with existing analytics
    location,
  });
}

interface LogChatMessageSentParams {
  /**
   * ID of the worker being chatted with
   */
  workerId: string;
  /**
   * ID of the workplace (admin user)
   */
  workplaceId: string;
  /**
   * Type of message sent
   */
  messageType: "text" | "file";
  /**
   * Whether this is a reply to another message
   */
  isReply: boolean;
}

/**
 * Logs when a message is sent in chat.
 * Tracks message type and whether it's a reply.
 */
export function logChatMessageSent(params: LogChatMessageSentParams) {
  const { workerId, workplaceId, messageType, isReply } = params;

  logEvent(CHAT_ANALYTICS_EVENTS.CHAT_MESSAGE_SENT, {
    workerId,
    workplaceId,
    messageType,
    isReply,
  });
}

interface LogChatMessageEditedParams {
  workerId: string;
  workplaceId: string;
}

/**
 * Logs when a message is edited in chat.
 */
export function logChatMessageEdited(params: LogChatMessageEditedParams) {
  const { workerId, workplaceId } = params;

  logEvent(CHAT_ANALYTICS_EVENTS.CHAT_MESSAGE_EDITED, {
    workerId,
    workplaceId,
  });
}

interface LogChatMessageDeletedParams {
  workerId: string;
  workplaceId: string;
}

/**
 * Logs when a message is deleted in chat.
 */
export function logChatMessageDeleted(params: LogChatMessageDeletedParams) {
  const { workerId, workplaceId } = params;

  logEvent(CHAT_ANALYTICS_EVENTS.CHAT_MESSAGE_DELETED, {
    workerId,
    workplaceId,
  });
}

interface LogConversationSelectedParams {
  workerId: string;
  workplaceId: string;
  placementId?: string;
  source: "channelList" | "deepLink";
}

/**
 * Logs when a conversation is selected in the full-page chat.
 */
export function logConversationSelected(params: LogConversationSelectedParams) {
  logEvent(CHAT_ANALYTICS_EVENTS.CHAT_CONVERSATION_SELECTED, params);
}

interface LogFilterChangedParams {
  filterType: string;
  filterValue: string;
  workplaceId: string;
}

/**
 * Logs when a channel list filter is changed.
 */
export function logFilterChanged(params: LogFilterChangedParams) {
  logEvent(CHAT_ANALYTICS_EVENTS.CHAT_FILTER_CHANGED, params);
}
